<template>
  <div class="flex flex-column gap-1">
    <label class="editor-label"
      >Escoja a qué variable hace referencia el filtro</label
    >
    <div class="sql-selector flex align-items-center flex-wrap">
      <div
        v-for="(string, index) in sql"
        :key="index"
        :class="{
          'sql-select-option': string.isOption,
          selected: string.selected,
          disabled: string.disabled,
          'special-char': string.isSpecialChar,
          'p-0': string.disabled || !string.isOption,
        }"
        @click="selectString(string)"
      >
        {{ string.value }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";

// #region Props, Emits
const props = defineProps({
  filters: {
    type: Array,
    default: () => [],
  },
  editFilter: {
    type: Object,
    default: null,
  },
  disabledVars: {
    type: Array,
    default: () => [],
  },
  showPickingMinOrMax: {
    type: Boolean,
    default: false,
  },
  pickingMinOrMax: {
    type: String,
    default: "",
  },
  preselectedVariable: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["selectString"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const sql = ref(null);
const store = useStore();
const sqlCode = computed(() => {
  const code = store.getters.sqlCode;
  return code.replaceAll(/\s{2,}/g, " ").trim();
});
const selectedOption = ref(null);
// #endregion

// #region Functions
function formatSqlCode() {
  // Replace line breaks with spaces
  const codeWithoutLineBreaks = sqlCode.value.replace(/\n/g, " ");
  let splitted = codeWithoutLineBreaks?.split(" ");

  if (splitted[splitted.length - 1].includes(";")) {
    splitted[splitted.length - 1] = splitted[splitted.length - 1].replace(
      ";",
      "",
    );

    splitted.push(";");
  }

  // Check for '%$variable', '$variable%' or '%$variable%' patterns and split them
  let processed = [];

  splitted.forEach((token) => {
    // Handle quoted patterns like '%$var%' or "$var%"
    const quotedMatch = token.match(/^(['"])(%?\$[A-Za-z0-9]+%?)\1$/);

    if (quotedMatch) {
      const core = quotedMatch[2]
        .replaceAll(/%(\$[A-Za-z0-9]+)/g, "% $1")
        .replaceAll(/(\$[A-Za-z0-9]+)%/g, "$1 %");

      core
        .split(/\s+/)
        .filter(Boolean)
        .forEach((part) =>
          processed.push(quotedMatch[1] + part + quotedMatch[1]),
        );

      return;
    }

    // Handle parentheses patterns like '($variable)' or '($variable'
    if (/^\(?\$[A-Za-z0-9]+\)?$/.test(token)) {
      const parts = [];

      if (token.startsWith("(")) {
        parts.push("(");
      }

      const variable = token.replace(/^\(/, "").replace(/\)$/, "");
      parts.push(variable);

      if (token.endsWith(")")) {
        parts.push(")");
      }

      processed.push(...parts);
      return;
    }

    // Handle quotes patterns like ''$variable'' or "'$variable"
    if (/^['"]?\$[A-Za-z0-9]+['"]?$/.test(token)) {
      const parts = [];
      const firstChar = token[0];
      const lastChar = token[token.length - 1];

      if (firstChar === "'" || firstChar === '"') {
        parts.push(firstChar);
      }

      const variable = token.replace(/^['"]/, "").replace(/['"]$/, "");
      parts.push(variable);

      if (lastChar === "'" || lastChar === '"') {
        parts.push(lastChar);
      }

      processed.push(...parts);
      return;
    }

    if (/^%?\$[A-Za-z0-9]+%?$/.test(token)) {
      const parts = [];

      if (token.startsWith("%")) {
        parts.push("%");
      }

      const variable = token.replace(/^%/, "").replace(/%$/, "");
      parts.push(variable);

      if (token.endsWith("%")) {
        parts.push("%");
      }

      processed.push(...parts);
    } else if (/%\$[A-Za-z0-9]+|\$[A-Za-z0-9]+%/.test(token)) {
      let working = token
        .replaceAll(/%(\$[A-Za-z0-9]+)/g, "% $1")
        .replaceAll(/(\$[A-Za-z0-9]+)%/g, "$1 %");

      working
        .split(/\s+/)
        .filter(Boolean)
        .forEach((part) => processed.push(part));
    } else {
      processed.push(token);
    }
  });

  splitted = processed;

  splitted = splitted.map((token) => {
    if (
      token.length >= 2 &&
      ((token.startsWith("'") && token.endsWith("'")) ||
        (token.startsWith('"') && token.endsWith('"')))
    ) {
      return token.slice(1, -1);
    }
    return token;
  });

  let sqlSplitted = [];

  for (const element of splitted) {
    const trimmedString = element.trim();

    let disabled = false;

    props.filters.forEach((filter) => {
      if (Array.isArray(filter.variable)) {
        if (
          trimmedString == filter.variable[0].value ||
          trimmedString == filter.variable[1].value
        ) {
          disabled = true;
        }
      } else if (filter.variable === trimmedString) {
        disabled = true;
      }
    });

    let selected = false;

    const variableIsArray = Array.isArray(props.editFilter?.variable);

    if (variableIsArray) {
      const existVariable = props.editFilter?.variable.find(v => v.value);
      if (existVariable) {
        if (trimmedString == existVariable.value) {
          disabled = false;
          selected = true;

          selectedOption.value = {
            value: trimmedString,
            selected,
            isOption: !!element.match(/\$[a-zA-Z0-9]+/g),
            disabled,
          };
        }
      }
    }

    else if (trimmedString == props.editFilter?.variable) {
      disabled = false;
      selected = true;

      selectedOption.value = {
        value: trimmedString,
        selected,
        isOption: !!element.match(/\$[a-zA-Z0-9]+/g),
        disabled,
      };
    }

    // Check if it's a special character that should be grouped
    const isSpecialChar = ['%', '(', ')', "'", '"'].includes(trimmedString);

    sqlSplitted.push({
      value: trimmedString,
      selected,
      isOption: !!element.match(/\$[a-zA-Z0-9]+/g),
      disabled,
      isSpecialChar,
    });
  }

  sql.value = sqlSplitted;
}

// Select a string
function selectString(string) {
  // If the string is an option, select it
  if (string.isOption && string.disabled === false) {
    const isUnselecting = string == selectedOption.value;

    if (isUnselecting) {
      selectedOption.value = null;
    } else {
      selectedOption.value = string;
    }

    // set selected to false for all strings
    sql.value.forEach((string) => {
      string.selected = false;
    });

    if (!isUnselecting) {
      // set selected to false for all strings
      sql.value.forEach((s) => {
        if (s.value == string.value) {
          s.selected = !s.selected;
        }
      });
    }
  }
}

function maxValue(sqlElement) {
  if (sqlElement == props.disabledVars[0].value) {
    sqlElement.disabled = true;
    sqlElement.selected = false;
  }

  if (sqlElement == props.disabledVars[1].value) {
    sqlElement.disabled = false;
    sqlElement.selected = true;
    selectedOption.value = sqlElement;
  }
}

function minValue(sqlElement) {
  if (sqlElement == props.disabledVars[0].value) {
    sqlElement.disabled = false;
    sqlElement.selected = true;
    selectedOption.value = sqlElement;
  }

  if (sqlElement == props.disabledVars[1].value) {
    sqlElement.disabled = true;
    sqlElement.selected = false;
  }
}
// #endregion

// #region Effects, Hooks
watch(sqlCode, () => {
  formatSqlCode();
});

watch(
  () => props.preselectedVariable,
  (newValue) => {
    if (newValue && sql.value) {
      sql.value.forEach((sqlElement) => {
        if (Array.isArray(newValue)) {
          if (sqlElement.value == newValue[0].value) {
            sqlElement.selected = true;
            selectedOption.value = sqlElement;
          }

          if (sqlElement.value == newValue[1].value) {
            sqlElement.disabled = true;
          }
        } else if (sqlElement.value == newValue) {
          sqlElement.selected = true;
          selectedOption.value = sqlElement;
        }
      });
    }
  },
);

watch(
  () => props.showPickingMinOrMax,
  () => {
    sql.value.forEach((sqlElement) => {
      if (sqlElement.isOption) {
        sqlElement.disabled = false;
        sqlElement.selected = false;
        selectedOption.value = null;
      }
    });
  },
);

// watch pickingMinOrMax
watch(
  () => props.pickingMinOrMax,
  (newValue) => {
    /*
      El objeto props.disabledVars tiene la siguiente estructura:
      [
        {
          "type": "min",
          "value": "" // Aquí va el nombre de la variable asignada al filtro mínimo
        },
        {
          "type": "max",
          "value": "" // Aquí va el nombre de la variable asignada al filtro máximo
        }
      ]
    */
    sql.value.forEach((sqlElement) => {
      if (sqlElement.isOption) {
        if (newValue == "Valor máximo") {
          maxValue(sqlElement);
        }

        if (newValue == "Valor mínimo") {
          minValue(sqlElement);
        }
      }
    });
  },
);

watch(selectedOption, (newValue) => {
  if (newValue) {
    emit("selectString", newValue.value);
  } else {
    emit("selectString", null);
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  selectedOption,
  formatSqlCode,
});
// #endregion
</script>

<style lang="scss">
@import "@/styles/_vars.scss";

.sql-selector {
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 0.5rem;
  font-family: monospace;
  font-size: 0.8rem;
  white-space: pre-wrap;
  gap: 0;
  row-gap: 1rem;

  .editor-label {
    color: #6b7280;
  }

  // Default spacing for all elements
  > div {
    margin-right: 0.5rem;
  }

  .sql-select-option {
    background-color: #e0e0e0;
    border-radius: 0.25rem;
    padding: 0.25rem;
    // padding: 0px;

    &:hover {
      background-color: #d0d0d0;
      cursor: pointer;
      transition: $transition;
    }

    &.disabled {
      background-color: #f5f5f5;
      color: #a0a0a0;
      cursor: not-allowed;
    }

    &.selected {
      background-color: $primary;
      color: white;
    }
  }

  // Special characters have no spacing and no background
  .special-char {
    padding: 0;
    margin-right: 0 !important;
    background-color: transparent;
    cursor: default;
    
    // When followed by a variable, no space
    &:has(+ .sql-select-option) {
      margin-right: 0 !important;
    }
  }

  // When a variable is followed by a special char, no space between them
  .sql-select-option:has(+ .special-char) {
    margin-right: 0 !important;
  }
}
</style>
